import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Loader2, Lock, Eye, EyeOff, ArrowLeft, Shield, ArrowRight, UserCog, AlertCircle,
} from 'lucide-react';

// Standalone manager (a.k.a. business "administrator") login page reached via
// a private URL the owner shares with staff. Route: /staff/:publicCode (BV-XXXXX).
// Users don't discover this page — it's linked from the owner's dashboard
// via the "Copy admin access link" action.
export default function ManagerLoginPage() {
  const { language } = useI18n();
  const { managerLogin } = useAuth();
  const navigate = useNavigate();
  const { publicCode } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [managers, setManagers] = useState([]);

  const [step, setStep] = useState(1); // 1: picker, 2: PIN
  const [pickedId, setPickedId] = useState('');
  const [pickedName, setPickedName] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const es = language === 'es';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAPI.getBusinessManagersByCode(publicCode);
        if (cancelled) return;
        setBusinessInfo({
          email: data.business_email,
          name: data.business_name,
          logo: data.business_logo_url,
        });
        setManagers(data.managers || []);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setError(es
            ? 'Este link no es válido. Pídele al dueño del negocio uno nuevo.'
            : 'This link is invalid. Ask the business owner for a new one.');
        } else {
          setError(es
            ? 'No pudimos cargar los administradores. Intenta de nuevo.'
            : "Couldn't load administrators. Please try again.");
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [publicCode, es]);

  const handlePick = (m) => {
    if (!m.has_pin) {
      toast.error(es
        ? 'Este administrador aún no tiene PIN. Pídele al dueño que se lo configure.'
        : 'This administrator has no PIN yet. Ask the owner to set one.');
      return;
    }
    setPickedId(m.id);
    setPickedName(m.name);
    setPin('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pin || pin.length < 4) {
      toast.error(es ? 'Ingresa tu PIN' : 'Enter your PIN');
      return;
    }
    setSubmitting(true);
    try {
      await managerLogin(businessInfo.email, pickedId, pin);
      toast.success(es ? `Bienvenido, ${pickedName}` : `Welcome, ${pickedName}`);
      navigate('/business/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      toast.error(detail || (es ? 'PIN incorrecto' : 'Wrong PIN'));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4 pt-20" data-testid="manager-login-page">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#F05D5E] transition-colors mb-3 group"
          data-testid="manager-login-back-btn"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          {es ? 'Ir al inicio' : 'Go home'}
        </button>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-3">
            {businessInfo?.logo ? (
              <Avatar className="h-14 w-14 mx-auto mb-2">
                <AvatarImage src={businessInfo.logo} alt={businessInfo.name} />
                <AvatarFallback className="bg-amber-100 text-amber-700 font-bold">
                  {businessInfo.name?.[0] || '?'}
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="h-14 w-14 mx-auto mb-2 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                <UserCog className="h-7 w-7" />
              </div>
            )}
            <CardTitle className="text-xl font-heading font-bold">
              {loading ? (es ? 'Cargando...' : 'Loading...')
                : businessInfo?.name || (es ? 'Acceso de Administrador' : 'Administrator Access')}
            </CardTitle>
            <CardDescription>
              {es ? 'Ingresa a la agenda del negocio con tu PIN personal' : 'Enter the business dashboard with your personal PIN'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#F05D5E]" />
              </div>
            )}

            {!loading && error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200/70 p-4 text-center">
                <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
                <Button variant="outline" className="mt-3" onClick={() => navigate('/')} data-testid="manager-login-error-home-btn">
                  {es ? 'Volver al inicio' : 'Back home'}
                </Button>
              </div>
            )}

            {!loading && !error && managers.length === 0 && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 p-4 text-center">
                <AlertCircle className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  {es
                    ? 'Este negocio aún no tiene administradores designados. Pídele al dueño que te agregue como administrador y te asigne un PIN.'
                    : 'This business has no administrators yet. Ask the owner to add you as an administrator and set a PIN.'}
                </p>
              </div>
            )}

            {/* Step 1 — picker */}
            {!loading && !error && managers.length > 0 && step === 1 && (
              <>
                <p className="text-sm font-medium">{es ? '¿Quién eres tú?' : 'Who are you?'}</p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {managers.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => handlePick(m)}
                      className="w-full flex items-center justify-between p-3 rounded-lg border border-border/70 hover:border-[#F05D5E] hover:bg-[#F05D5E]/5 transition-colors text-left"
                      data-testid={`manager-pick-${m.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-bold">
                          {m.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{m.name}</p>
                          {!m.has_pin && (
                            <p className="text-[11px] text-red-600">
                              {es ? 'Sin PIN asignado' : 'No PIN set'}
                            </p>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Step 2 — PIN */}
            {!loading && !error && step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">{es ? 'Ingresando como' : 'Signing in as'}</p>
                  <p className="text-sm font-semibold">{pickedName}</p>
                  {businessInfo?.name && (
                    <p className="text-xs text-muted-foreground">@ {businessInfo.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mgr-pin">{es ? 'Tu PIN personal' : 'Your personal PIN'}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      id="mgr-pin"
                      type={showPin ? 'text' : 'password'}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      placeholder="••••"
                      value={pin}
                      onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                      className="flex h-14 w-full rounded-md border border-input bg-transparent pl-10 pr-10 py-1 text-xl tracking-widest text-center shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      required
                      autoFocus
                      data-testid="manager-pin-input"
                    />
                    <button type="button" onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full h-12 btn-coral text-base" disabled={submitting} data-testid="manager-pin-submit-btn">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <><Shield className="h-5 w-5 mr-2" />{es ? 'Entrar' : 'Enter'}</>
                  )}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setStep(1)} data-testid="manager-back-picker-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {es ? 'Cambiar de usuario' : 'Change user'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
